import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import MyPiggyIcon from '../UI/MyPiggyIcon';
import '../../styles/brand-typography.css';
import './KidSetup.css';

export const KID_TOUR_STEPS = [
  {
    key: 'tour_balance',
    selector: '[data-tour="kid-balance"]',
    icon: 'balance',
    title: 'Your Piggy Bank Money',
    description:
      'See your Available Money and the money you have saved for goals right here.',
  },
  {
    key: 'tour_add_money',
    selector: '[data-tour="kid-add-money"]',
    icon: 'add-money',
    title: 'Add Money',
    description:
      'Got allowance, a gift, or a bonus? Add it here. For money earned from chores, use Do Chores.',
  },
  {
    key: 'tour_spent_money',
    selector: '[data-tour="kid-spent-money"]',
    icon: 'spent-money',
    title: 'Spent Money',
    description:
      'Bought something with your Piggy Bank money? Record it here so you know how much you have left.',
  },
  {
    key: 'tour_savings',
    selector: '[data-tour="kid-savings"]',
    icon: 'my-savings',
    title: 'My Savings',
    description:
      'Save for something you really want and watch your progress grow.',
  },
  {
    key: 'tour_chores',
    selector: '[data-tour="kid-chores"]',
    icon: 'do-chores',
    title: 'Do Chores',
    description:
      'Finished a chore? Press I Did It! next to it. Your parent may need to approve the reward. You can also add your own chore.',
  },
  {
    key: 'tour_reports',
    selector: '[data-tour="kid-reports"]',
    icon: 'view-my-reports',
    title: 'View My Reports',
    description:
      'See how you earn, spend, and save your money over time.',
  },
  {
    key: 'tour_settings',
    selector: '[data-tour="kid-settings"]',
    icon: 'settings',
    title: 'Your Settings',
    description:
      'Change your avatar and theme, match your Piggy Bank money, find our Kids and Parents apps, or replay this tour in Settings.',
  },

];

const getTargetRect = (selector) => {
  const element = document.querySelector(selector);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

export default function KidDashboardTour({
  open,
  initialStepKey = 'tour_balance',
  onStepChange,
  onFinish,
  onViewApps,
  onExit,
  finishLabel = 'Personalize MyPiggy',
}) {
  const initialIndex = Math.max(
    0,
    KID_TOUR_STEPS.findIndex(
      (item) => item.key === initialStepKey
    )
  );
  const [stepIndex, setStepIndex] = useState(initialIndex);
  const [targetRect, setTargetRect] = useState(null);
  const [cardHeight, setCardHeight] = useState(240);
  const cardRef = useRef(null);
  const step = KID_TOUR_STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === KID_TOUR_STEPS.length - 1;

  useEffect(() => {
    if (open) setStepIndex(initialIndex);
  }, [initialIndex, open]);

  useEffect(() => {
    if (!open || !step) return undefined;

    const target = document.querySelector(step.selector);
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    const updateRect = () => {
      setTargetRect(getTargetRect(step.selector));
    };

    updateRect();
    const timer = window.setTimeout(updateRect, 400);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    onStepChange?.(step.key, stepIndex);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [onStepChange, open, step, stepIndex]);

  useEffect(() => {
    if (!open || !cardRef.current) return undefined;

    const updateHeight = () => {
      const height = cardRef.current?.getBoundingClientRect().height;
      if (height) setCardHeight(height);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [open, stepIndex]);

  const cardPosition = useMemo(() => {
    const padding = 16;
    const gap = 14;
    const width = Math.min(440, window.innerWidth - padding * 2);

    if (!targetRect) {
      return {
        top: '50%',
        left: '50%',
        width,
        transform: 'translate(-50%, -50%)',
      };
    }

    const targetBottom = targetRect.top + targetRect.height;
    const roomBelow = window.innerHeight - targetBottom - gap - padding;
    const roomAbove = targetRect.top - gap - padding;
    let top;

    if (roomBelow >= cardHeight) {
      top = targetBottom + gap;
    } else if (roomAbove >= cardHeight) {
      top = targetRect.top - gap - cardHeight;
    } else if (roomBelow >= roomAbove) {
      top = targetBottom + gap;
    } else {
      top = targetRect.top - gap - cardHeight;
    }

    top = Math.max(
      padding,
      Math.min(top, window.innerHeight - cardHeight - padding)
    );

    const left = Math.max(
      padding,
      Math.min(
        targetRect.left + targetRect.width / 2 - width / 2,
        window.innerWidth - width - padding
      )
    );

    return { top, left, width };
  }, [cardHeight, targetRect]);

  if (!open || !step) return null;

  return (
    <div
      className="mypiggy-brand kid-setup fixed inset-0 z-[200]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kid-tour-title"
      aria-describedby="kid-tour-description"
    >
      {!targetRect && (
        <div className="absolute inset-0 bg-slate-950/70" />
      )}

      {targetRect && (
        <div
          className="fixed rounded-xl ring-4 ring-white shadow-[0_0_0_9999px_rgba(15,23,42,0.72)] pointer-events-none transition-all duration-200"
          style={{
            top: Math.max(6, targetRect.top - 6),
            left: Math.max(6, targetRect.left - 6),
            width: Math.min(window.innerWidth - 12, targetRect.width + 12),
            height: Math.min(window.innerHeight - 12, targetRect.height + 12),
          }}
        />
      )}

      <div
        ref={cardRef}
        className="fixed max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl"
        style={cardPosition}
      >
        <div className="flex items-start gap-3">
          <MyPiggyIcon name={step.icon} size="md" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold uppercase tracking-wide text-purple-600">
              {stepIndex + 1} of {KID_TOUR_STEPS.length}
            </div>
            <h2 id="kid-tour-title" className="mt-1 text-xl font-extrabold text-gray-900">
              {step.title}
            </h2>
            <p id="kid-tour-description" className="mt-2 text-base font-bold leading-relaxed text-slate-600">
              {step.description}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={onExit}
            className="text-sm font-bold text-gray-500 hover:text-gray-800"
          >
            Finish Later
          </button>

          <div className="flex flex-wrap gap-2">
            {isLast && onViewApps && <button type="button" onClick={onViewApps} className="rounded-xl border border-teal-700 px-4 py-2 text-sm font-extrabold text-teal-800">Finish &amp; View Apps</button>}
            {!isFirst && (
              <button
                type="button"
                onClick={() => setStepIndex((value) => Math.max(0, value - 1))}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (isLast) onFinish?.();
                else setStepIndex((value) => value + 1);
              }}
              className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-bold text-white hover:bg-purple-700"
            >
              {isLast ? finishLabel : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

